import { Check, Languages } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { LOCALE_LABELS, LOCALES, useI18n } from '../i18n'

/**
 * Three-option language switcher (EN / 繁體中文 / 简体中文). Swaps copy client-side
 * with no reload; selection persists (localStorage) and updates `html lang` via
 * the I18nProvider. Keyboard-operable: opens on click, closes on Esc / outside
 * click, options are real buttons.
 */
export default function LanguageToggle() {
  const { locale, setLocale, t } = useI18n()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="lang-wrap" ref={wrapRef}>
      <button
        type="button"
        className="chip-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('nav.language')}
        title={t('nav.language')}
      >
        <Languages />
        <b>{LOCALE_LABELS[locale].short}</b>
      </button>
      {open && (
        <div className="lang-menu" role="menu">
          {LOCALES.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={code === locale}
              className={code === locale ? 'on' : ''}
              onClick={() => {
                setLocale(code)
                setOpen(false)
              }}
            >
              <span>{LOCALE_LABELS[code].full}</span>
              {code === locale && <Check />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
